import React, { useCallback, useState } from "react";
import {
  Button,
  FormGroup,
  HTMLSelect,
  Label,
  TextArea,
} from "@blueprintjs/core";
import { useMutation, useQueryClient } from "react-query";
// import { TASKS_QUERY_FILTER } from 'ui-io/queries/tasks/tasks-query'
import { Box, InlineSpinnerIcon, ThemedDialog } from "ui-components";
import { delay } from "ui-lib/delay";
import { TaskT } from "ui-io/task-io/task-t";
// import { useSessionState } from '@maps-internal/neutron-react-client'
import useSession from "ui-io/use-session";
import { useNavigate } from "react-router-dom";
import { RejectReason } from "ui-io/task-io";
import produce from "immer";
// import { remove } from 'ramda'
import { taskEventMutation } from "ui-io/queries/tasks/task-event-mutation";
import { taskAddTagMutation } from "ui-io/queries/tasks/task-add-tag-mutation";

import toasts from "../../utils/toasts";
import { TASKS_QUERY_FILTER } from "ui-io/queries/tasks/task-query";
import RejectionReasonInput from "../need-review/rejection-reason-input";
import { TASKS_SEARCH_QUERY } from "ui-io/queries/tasks/tasks-query";
import { formatLocation } from "../../routes";

const newReason = { field: "", reason: "" };

const RejectSubmissionButton = ({ task }: { task: TaskT }) => {
  const { isReadyForPublish, assignee } = task;
  // const { username } = useSessionState()
  const { session } = useSession();
  const username = session?.username || "";
  const rejectFlags = [
    "REJECT_FLAG-Select",
    "REJECT_FLAG-CONFIG_ISSUE",
    "REJECT_FLAG-DATA_QUALITY",
  ];
  const isInReview = isReadyForPublish;
  const history = useNavigate();
  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);
  const [reasons, setReason] = useState<RejectReason[]>([]);
  const [rejectFlag, setRejectFlag] = useState(rejectFlags[0]);
  const [comment, setComment] = useState<string>();
  const addTagMutation = useMutation(taskAddTagMutation, {
    onError: (err) => {
      toasts.error(`Unable to add flag: ${err}`);
    },
    onSuccess: () => {
      toasts.success("Flag added successfully");
      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
    },
  });

  const queryClient = useQueryClient();
  const mutation = useMutation(
    async function ({ task }: { task: TaskT }) {
      let properties = {
        comment: comment || undefined,
        reject_reasons: reasons,
      };
      if (task.isReadyForPublish) {
        return await taskEventMutation({
          taskId: task.id,
          eventType: "REJECTED_BY_PUBLISHER",
          updatedBy: username,
        });
        // TODO: request to create notes with properties
      } else {
        throw new Error(
          `Cannot reject submission for task in state "${task.state}"`
        );
      }
    },
    {
      onError: (error) => {
        console.error(error);
        toasts.error(`Encountered an error when rejecting a submission`);
      },
      onSuccess: async () => {
        toasts.success(`Successfully rejected submission`);
        await delay(500);
        queryClient.refetchQueries([TASKS_QUERY_FILTER]);
        queryClient.refetchQueries([TASKS_SEARCH_QUERY]);
        history(formatLocation.taskPeerReview({}));
      },
    }
  );
  const disabled = !isInReview || mutation.isLoading;

  const handleRequestNewTaskButtonClick = useCallback(async () => {
    if (disabled) {
      return;
    }
    setIsRejectionDialogOpen(true);
    await mutation.mutateAsync({
      task,
    });
    if (rejectFlag !== "REJECT_FLAG-Select") {
      addTagMutation.mutate({
        taskId: task._raw.task_id,
        flag: rejectFlag,
      });
    }
  }, [addTagMutation, disabled, mutation, rejectFlag, task]);

  if (!isInReview || (username !== assignee && !task.isReadyForPublish)) {
    return null;
  }
  const areReasonsComplete = reasons.every(
    (reason) => reason.field && reason.reason
  );
  const setReasonAtIndex = (i: number) => (reason: RejectReason) => {
    setReason(
      produce((draft) => {
        draft[i] = reason;
      })
    );
  };
  // const deleteReasonAtIndex = (i: number) => () => setReason(remove(i, 1))
  const deleteReasonAtIndex = (i: number) => () =>
    setReason((reasons) => [...reasons.slice(0, i), ...reasons.slice(i + 1)]);

  return (
    <>
      <Box mt="large">
        <ThemedDialog
          isOpen={isRejectionDialogOpen}
          title="Rejection reason"
          onClose={() => setIsRejectionDialogOpen(false)}
          canEscapeKeyClose
        >
          <Box p="medium" pb="none">
            <FormGroup label="Rejection reason">
              {(areReasonsComplete ? [...reasons, newReason] : reasons).map(
                (reason, i) => (
                  <RejectionReasonInput
                    key={i}
                    reason={reason}
                    onChange={setReasonAtIndex(i)}
                    onDelete={
                      i < reasons.length ? deleteReasonAtIndex(i) : undefined
                    }
                  />
                )
              )}
            </FormGroup>
            {task.isReadyForPublish && (
              <FormGroup label="Reject flag">
                <HTMLSelect onChange={(e) => setRejectFlag(e.target.value)}>
                  {rejectFlags.map((flag, index) => (
                    <option value={flag} key={index}>
                      {flag.split("-")[1]}
                    </option>
                  ))}
                </HTMLSelect>
              </FormGroup>
            )}

            <Box mt={2}>
              <Label>Comment</Label>
              <TextArea
                fill
                name="comment"
                value={comment}
                rows={2}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Reason for rejecting this task"
              />
            </Box>
            <Box flexDir="row" justify="between">
              <Box mt="medium" justify="end">
                <Button onClick={() => setIsRejectionDialogOpen(false)}>
                  Close
                </Button>
                <Box ml="small">
                  <Button
                    intent="danger"
                    disabled={reasons.length < 1}
                    onClick={handleRequestNewTaskButtonClick}
                  >
                    {mutation.isLoading ? (
                      <>
                        Rejecting
                        <Box mx="small" inline>
                          <InlineSpinnerIcon />
                        </Box>
                      </>
                    ) : (
                      "Reject"
                    )}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </ThemedDialog>
      </Box>
      <Button
        icon="cross"
        onClick={() => setIsRejectionDialogOpen(true)}
        disabled={disabled}
        intent="danger"
      >
        Reject
      </Button>
    </>
  );
};

export default RejectSubmissionButton;
